import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs/operators';
import { KeycloakPasswordAuthService } from './keycloak-password-auth.service';
import { environment } from '../../../environments/environment';

/**
 * Attaches the Keycloak access token to every backend call under `/api/v1/*`.
 * Calls without a token (anonymous browser session, or hitting `/assets/*`,
 * or the Keycloak host itself) pass through untouched — booking-service will
 * 401 on the protected endpoints, which is what we want during the slice.
 *
 * Skips:
 *   - `/assets/*` (i18n bundles and static files don't need auth).
 *   - The Keycloak authority URL itself (token endpoint, JWKS, etc. — the
 *     OIDC client library does its own auth there).
 *   - Anything not under `/api/` (Angular's dev server, third-party calls).
 *
 * Replaces / complements the existing `acceptLanguageInterceptor` — they
 * compose: acceptLanguageInterceptor adds `Accept-Language`, this one adds
 * `Authorization: Bearer`. Both run on the same request.
 */
export const keycloakBearerInterceptor: HttpInterceptorFn = (req, next) => {
  if (!shouldAttach(req.url)) {
    return next(req);
  }
  const oidc = inject(OidcSecurityService);
  const password = inject(KeycloakPasswordAuthService);
  return oidc.getAccessToken().pipe(
    take(1),
    switchMap((oidcToken) => {
      // Prefer the active password session. The sign-in form uses ROPC and
      // persists its token independently of the OIDC client; preferring the
      // OIDC token here can reuse a stale token left by an earlier session.
      // Keep the OIDC token as fallback for SSO-only sessions.
      const passwordToken = password.sessionAccessToken();
      const token = passwordToken || oidcToken;
      if (!token) return next(req);
      return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
    }),
  );
};

function shouldAttach(url: string): boolean {
  if (url.startsWith('/assets/')) return false;
  // Legacy flight search / cheapest endpoints are intentionally public on
  // flight-service. Attaching a stale bearer here can turn an otherwise
  // valid anonymous request into a 401/403 when the stored token no longer
  // matches the active issuer / signing keys after an environment move.
  // Keep these calls anonymous from B2B.
  if (url.startsWith('/api/v1/flights/legacy/')) return false;
  // Same trap, bus-service: it validates bearers against the **ota-b2c** realm only
  // (PBS_JWT_JWK_SET_URI), so a B2B console token from ota-engine has "no matching key(s)" and the
  // bearer filter 401s the request BEFORE the permitAll rule is ever consulted. Both endpoints B2B
  // uses here — GET /stations and POST /search — are permitAll by design, which is why the same
  // calls succeed from B2C: B2C sends no token at all. Keep them anonymous.
  if (url.startsWith('/api/v1/buses/')) return false;
  // Calls to Keycloak itself (e.g. /.well-known/, /protocol/) go via the
  // OIDC client; we don't double-stamp the Authorization header. Exclude the
  // configured authority (dev localhost:8180 or the prod public host).
  if (environment.keycloakAuthority && url.startsWith(environment.keycloakAuthority)) return false;
  if (url.startsWith('http://localhost:8180/') || url.startsWith('https://localhost:8180/')) return false;
  // Relative /api/* paths — dev proxy in local, rewritten onto the gateway in prod.
  if (url.startsWith('/api/')) return true;
  // Production api-gateway host: the search *BaseUrl values (and gateway-rewritten
  // /api calls) are absolute under apiBaseUrl and still need the token.
  if (environment.apiBaseUrl && url.startsWith(environment.apiBaseUrl)) return true;
  // Own-backend services hit directly by absolute localhost URL (e.g.
  // http://localhost:8102/api/... for hotel-service, which isn't behind the
  // dev proxy) ALSO need the token. Services running ota-security-starter's
  // dev-bypass forward it to identity-service's /users/{sub}/permissions
  // resolve — and since identity now runs as a JWT-only container, that resolve
  // 401s without a token, leaving the caller with no PERM_* authorities (so
  // platform-scope / @PreAuthorize gates wrongly 403). Our own services only:
  // localhost / 127.0.0.1 on any port (Keycloak :8180 already excluded above);
  // external hosts (Tripall, etc.) stay untouched.
  if (/^https?:\/\/(localhost|127\.0\.0\.1):\d+\/api\/v1\/flights\/legacy\//i.test(url)) return false;
  return /^https?:\/\/(localhost|127\.0\.0\.1):\d+\/api\//i.test(url);
}
